import {
  AltiusRes,
  IFileMetaDetail,
  IFolder,
  IUpdateFileData,
  IWorkspaceMetadata,
  UpdateFolderData
} from "store/types";
import { toFormData } from "utils";

import { ClientAction } from "..";
import { AsyncActions } from "../base";

export interface IClientWorkspaceActions {
  getClientFoldersAction: (data: { clientId: number; disableDispatch?: boolean }) => Promise<IFolder[]>;
  // Deal-workspace-shaped responses: IWorkspaceMetadata / IFolder / IFileMetaDetail.
  /**
   * Folder contents shown in the workspace table.
   * `sectionId`: null → workspace root, -1 → all files, a number → that folder's contents.
   * `recently_added` with `sectionId: -1` → the Recently Added view.
   */
  getClientFolderMetadataAction: (data: {
    clientId: number;
    sectionId: number | null;
    /** Scalar API payload built by the context's `filterPayloadBuilder`. */
    filters: Record<string, unknown>;
    disableDispatch?: boolean;
    recently_added?: boolean;
    page?: number;
    deal_ids?: number[];
    sort_by?: string;
    sort?: "asc" | "desc";
  }) => Promise<IWorkspaceMetadata>;
  createClientWorkspaceFolderAction: (data: {
    clientId: number;
    title: string;
    parent_id?: number | null;
  }) => Promise<IFolder>;
  updateClientWorkspaceFolderAction: (
    data: { clientId: number } & Omit<UpdateFolderData, "dealId">
  ) => Promise<IFolder>;
  /** Single-file upload, same shape as the deal `uploadFileAction` (POST /clients/:id/workspace/files). */
  uploadClientWorkspaceFileAction: (data: {
    clientId: number;
    file: File;
    section_id?: number | null;
    document_type?: string;
    investor_id?: number;
    effective_date?: string | null;
    mode?: string;
  }) => Promise<IFileMetaDetail>;
  updateClientWorkspaceFileAction: (data: Omit<IUpdateFileData, "dealId" | "deal_id">) => Promise<IFileMetaDetail>;
  downloadClientWorkspaceFileAction: (data: { clientId: number; fileId: number }) => Promise<{ file_url: string }>;
  /** Patch one folder of the cached tree in place (e.g. after a rename) — no request. */
  patchClientWorkspaceFolderAction: (data: { id: number; patch: Partial<IFolder> }) => void;
}

export class ClientWorkspaceActions extends AsyncActions implements IClientWorkspaceActions {
  public getClientFoldersAction: IClientWorkspaceActions["getClientFoldersAction"] = async ({
    clientId,
    disableDispatch = false,
    ...params
  }) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<Record<number, IFolder>>>({
      url: `/api/v0.0.3/clients/${clientId}/folders`,
      params
    });
    if (!disableDispatch) {
      this.dispatch({ type: ClientAction.SetWorkspaceFolders, payload });
    }
    return Object.values(payload);
  };

  public getClientFolderMetadataAction: IClientWorkspaceActions["getClientFolderMetadataAction"] = async ({
    clientId,
    sectionId,
    recently_added,
    filters,
    page,
    sort_by,
    sort
  }) => {
    // POST body: section_id null → workspace folder, -1 → all files, number → folder.
    const params: Record<string, unknown> = { page, sort_by, sort, section_id: sectionId };
    if (recently_added) params.recently_added = recently_added;
    if (filters) Object.assign(params, filters);

    const {
      data: { data }
    } = await this.api.post<AltiusRes<IWorkspaceMetadata>>({
      url: `/api/v0.0.3/clients/${clientId}/workspace-folders`,
      data: params
    });
    return data;
  };

  public createClientWorkspaceFolderAction: IClientWorkspaceActions["createClientWorkspaceFolderAction"] = async ({
    clientId,
    title,
    parent_id
  }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IFolder>, { title: string; parent_id?: number | null }>({
      url: `/api/v0.0.3/clients/${clientId}/workspace/folders`,
      data: { title, parent_id }
    });
    return data;
  };

  public updateClientWorkspaceFolderAction: IClientWorkspaceActions["updateClientWorkspaceFolderAction"] = async ({
    clientId,
    folderId,
    ...body
  }) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<IFolder>, typeof body>({
      url: `/api/v0.0.3/clients/${clientId}/workspace/folders/${folderId}`,
      data: body
    });
    return data;
  };

  public uploadClientWorkspaceFileAction: IClientWorkspaceActions["uploadClientWorkspaceFileAction"] = async ({
    clientId,
    file,
    ...rest
  }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IFileMetaDetail>>({
      url: `/api/v0.0.3/clients/${clientId}/workspace/files`,
      data: toFormData({ ...rest, file }),
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    return data;
  };

  public updateClientWorkspaceFileAction: IClientWorkspaceActions["updateClientWorkspaceFileAction"] = async ({
    fileId,
    ...body
  }) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<IFileMetaDetail>, typeof body>({
      url: `/api/v0.0.3/files/${fileId}`,
      data: body
    });
    return data;
  };

  public downloadClientWorkspaceFileAction: IClientWorkspaceActions["downloadClientWorkspaceFileAction"] = async ({
    clientId,
    fileId
  }) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<{ file_url: string }>>({
      url: `/api/v0.0.3/clients/${clientId}/workspace/files/${fileId}/download`
    });
    return data;
  };

  public patchClientWorkspaceFolderAction: IClientWorkspaceActions["patchClientWorkspaceFolderAction"] = (payload) => {
    this.dispatch({ type: ClientAction.PatchWorkspaceFolder, payload });
  };
}
